import * as XLSX from 'xlsx'
import { CollectionConfig, ClientCollectionConfig, CollectionSlug, CustomComponent, TextField, ConditionalDateProps } from 'payload'
import { ExportableProps } from '@components/(payload)/list/exportable'
import { flattenPaginatedDocs, getCollectionPlural, getCollectionSingular } from '@utils/collections'
import { toDateString, toStringComparableDate } from '@utils/dates'
import { flattenCollectionFieldClient, FlattenCollectionFieldClientWithPathWithArray, formatCnpjField, formatCpfField, formatCurrencyField, formatDocumentField, getFieldLabel, mapCollectionFieldsClient, sortCollectionFieldClient } from '@utils/fields'
import { abbreviate, textList, trim } from '@utils/misc'
import { flattenObjectList, flattenOptionsArray, maybeArray, findObject, isObjectEmpty, isObject, mergeObjects } from '@utils/objects'

export type { ExportableProps } from '@components/(payload)/list/exportable'

type ExportableFieldFormats = 'currency' | 'document' | 'cpf' | 'cnpj' | `entity.${string}`;

type ExportableFieldPropsObject = {
  label: string;
  format?: ExportableFieldFormats;
} | {
  label?: string;
  format: ExportableFieldFormats;
};

/**
 * Exportable field options.
 *
 * Accept a object:
 * - label: Column name
 * - format: Value special format
 *
 * Or a string as "label".
 */
export type ExportableFieldProps = string | boolean | ExportableFieldPropsObject;

type ExportableRowData = Record<string, any>;
type ExportableData = Record<string, Record<string, ExportableRowData>>;

// Make a Collection Exportable/Importable
export const enableExport = (collection: CollectionConfig, options: ExportableProps): CollectionConfig => {
  collection.admin = collection.admin || {};
  collection.hooks = collection.hooks || {};

  // Add component
  const component: CustomComponent = {
    path: '@components/(payload)/list/exportable',
    clientProps: options,
  };

  collection.admin.components = collection.admin.components || {};
  collection.admin.components.afterList = [
    component,
    ...(collection.admin.components.afterList || [])
  ];

  return collection;
}

export const addItemRowToData = (
  allData: ExportableData,
  item: PayloadEntity | PayloadEntity[],
  collection: ClientCollectionConfig,
  collectionConfigs: ClientCollectionConfig[],
  sheetName?: string,
  parent?: { label: string, data: Partial<PayloadEntity> }
): ExportableData => {
  // Support list of items seamsly
  if (Array.isArray(item)) {
    for (let i = 0; i < item.length; i++) {
      allData = addItemRowToData(allData, item[i], collection, collectionConfigs, sheetName, parent);
    }

    return allData;
  }

  // For one entity
  const data = flattenPaginatedDocs(item);

  const singular = getCollectionSingular(collection) || 'Item';
  const plural = getCollectionPlural(collection) || 'Itens';

  sheetName = sheetName || plural;

  // Create page in order
  allData[sheetName] = allData[sheetName] || [];

  const useAsTitle = collection.admin?.useAsTitle || 'title';

  const collectionFields = sortCollectionFieldClient<FlattenCollectionFieldClientWithPathWithArray>(flattenCollectionFieldClient(collection.fields, false), ['id', useAsTitle]);
  const row: ExportableRowData = {};
  const rowId: string = trim(`${ parent ? parent.data.id : '' }-${data.id}`, '- ');

  // Have a parent
  if (parent) {
    row[parent.label] = parent.data.id;
  }

  for (let i = 0; i < collectionFields.length; i++) {
    const collectionField = collectionFields[i] as any;
    collectionField.admin = collectionField.admin || {};

    // Ignore not named fields
    if ('name' in collectionField === false) {
      continue;
    }

    const field: PayloadNamedClientField = findObject(collectionFields, 'name', collectionField.name) as PayloadNamedClientField;
    if (! field) {
      continue;
    }

    field.admin = field.admin || {};

    const name: string = field.name;

    // There is no data
    if (typeof data[name] === 'undefined') {
      continue;
    }

    // Skip hidden/virtual fields
    const isHidden = field.admin?.hidden;
    const isVirtual = 'virtual' in field && Boolean(field.virtual);
    const isIdOrTimestampField = [useAsTitle, 'id', 'createdAt', 'updatedAt'].includes(name);
    const isForcedHidden = 'exportable' in field.admin && typeof field.admin.exportable === 'boolean' && field.admin.exportable === false;
    const isForcedExportable = 'exportable' in field.admin && (field.admin.exportable === true || typeof field.admin.exportable === 'string');

    if (((isHidden || isVirtual) && ! isIdOrTimestampField && ! isForcedExportable) || isForcedHidden) {
      continue;
    }

    // Parse Exportable Options
    const exportableOptions: ExportableFieldPropsObject = { label: '' };
    collectionField.admin.exportable = collectionField.admin.exportable || exportableOptions;

    if (typeof collectionField.admin.exportable === 'string') {
      exportableOptions.label = collectionField.admin.exportable;
    }

    if (typeof collectionField.admin.exportable === 'object' && 'format' in collectionField.admin.exportable) {
      exportableOptions.format = collectionField.admin.exportable.format;
    }

    let value = data[name];
    const label = exportableOptions.label || getFieldLabel(collectionField);

    /**
     * Check for "entity.*" format in exportable options.
     * This will ignore array / join / relationship fields to just try to get data from item props.
     * Will return NULL if it's not configured.
     */
    if ('format' in exportableOptions && typeof exportableOptions.format === 'string' && exportableOptions.format.startsWith('entity.')) {
      const parts = exportableOptions.format.split('.') || [];
      if (parts.length !== 2) {
        throw new Error(`Invalid format for exportable field ${name}: ${exportableOptions.format}.`);
      }

      const props = parts[1];
      row[label] = maybeArray(value, item => {
        const value = item ? (typeof item[props] === 'undefined' ? '' : item[props]) : item;

        if (typeof value === 'undefined' || value === null) {
          return '';
        }

        if (! Array.isArray(value)) {
          return value;
        }

        return textList(value, { comma: ' | ', and: ' | ' });
      });

      continue;
    }

    // FIELD: ARRAY
    if (field.type === 'array') {
      if (! value || (Array.isArray(value) && ! value.length)) {
        continue;
      }

      // Get array data
      const arrayName = field.name;
      const arrayFields = flattenCollectionFieldClient((field.fields || []).filter(v => ! ('name' in v) || v.name !== 'id' ));

      // Create a fake collection for simple arrays
      const arrayCollection = {
        slug: field.name,
        label: field?.label || arrayName,
        labels: {
          singular: exportableOptions.label || field?.labels?.singular || arrayName,
          plural: exportableOptions.label || field?.labels?.plural || arrayName,
        },
        fields: arrayFields,
      };

      if (arrayFields.length === 1 && ! arrayFields[0]?.label) {
        arrayFields[0].label = field?.label || arrayName;
      }

      const arraySheetName = `${plural} - ${ arrayCollection.label || arrayCollection.labels.plural }`

      allData = addItemRowToData(allData, value, arrayCollection as any, collectionConfigs, arraySheetName, { label: singular, data });
      continue;
    }

    // FIELD: DATE
    if (field.type === 'date') {
      const format = (field.admin?.date as ConditionalDateProps)?.displayFormat;
      value = value ? toDateString(value, format) : value;
    }

    // FIELD: RELATIONSHIPS
    if (field.type === 'relationship' || field.type === 'upload') {
      if (! value || (Array.isArray(value) && ! value.length)) {
        continue;
      }

      // Get relationship data
      const relationshipCollection = findObject(collectionConfigs, 'slug', field.relationTo);
      if (! relationshipCollection) {
        value = JSON.stringify(value);
        continue;
      }

      if (field.type === 'upload') {
        relationshipCollection.fields = mapCollectionFieldsClient(relationshipCollection.fields, (field) => {
          if ('name' in field && ['url', 'filesize', 'filename', 'mimeType'].includes(field.name)) {
            field as CustomPayloadField<TextField>;

            field.admin = field.admin || {};
            field.admin.exportable = true;
          }

          return field;
        });
      }

      const relationshipData = maybeArray(value, v => (isObject(v) ? v : { id: v }));
      const relationshipSheetName = `${plural} - ${ getCollectionPlural(relationshipCollection) || name }`;

      allData = addItemRowToData(allData, relationshipData as (PayloadEntity | PayloadEntity[]), relationshipCollection, collectionConfigs, relationshipSheetName, { label: singular, data });
      continue;
    }

    // FIELD: JOIN
    if (field.type === 'join') {
      if (! value || (Array.isArray(value) && ! value.length)) {
        continue;
      }

      // Get join data
      const joinCollection = findObject(collectionConfigs, 'slug', field.collection);
      if (! joinCollection) {
        value = JSON.stringify(value);
        continue;
      }

      const on = field.on || collection.slug;
      const joinData = maybeArray(value, v => {
        if (! isObject(v)) {
          v = { id: v };
        }

        delete v[on];
        return v;
      });

      const joinSheetName = `${plural} - ${ getCollectionPlural(joinCollection) || name }`;

      allData = addItemRowToData(allData, joinData, joinCollection, collectionConfigs, joinSheetName, { label: singular, data });
      continue;
    }

    // FIELD: SELECT
    if (field.type === 'select') {
      const options = flattenOptionsArray(field.options);
      value = maybeArray(value, item => options[item] || item);
    }

    // FIELD: CHECKBOX
    if (field.type === 'checkbox') {
      value = maybeArray(value, item => item ? 'Sim' : 'Não');
    }

    // FIELD: JSON
    if (field.type === 'json') {
      value = JSON.stringify(value);
    }

    // Format fields
    value = maybeArray(value, item => {
      if (field.type === 'number') {
        item = item ? item.toString() : '';
      }

      if (typeof exportableOptions.format !== 'string' || typeof item !== 'string') {
        return item;
      }

      if (exportableOptions.format === 'currency') {
        return formatCurrencyField(item);
      }

      if (exportableOptions.format === 'document') {
        return formatDocumentField(item);
      }

      if (exportableOptions.format === 'cpf') {
        return formatCpfField(item);
      }

      if (exportableOptions.format === 'cnpj') {
        return formatCnpjField(item);
      }

      return item;
    });

    // Stringify if somethig was wrong and create a list with separators (easy to handle in Excel)
    if (Array.isArray(value)) {
      value = value.map(v => typeof v === 'string' ? v : ( (typeof v === 'undefined' || v === null) ? '' : JSON.stringify(v) ) );
      value = textList(value, { comma: ' | ', and: ' | ' });
    }

    row[label] = (typeof value === 'undefined' || value === null) ? '' : value;
  }

  // Ignore empty lines
  if (! isObjectEmpty(row)) {
    allData[sheetName][rowId] = mergeObjects(allData[sheetName][rowId] || {}, row);
  }

  return allData;
}

/**
 * Prepare data to be exported.
 * Will return a object with "labels" => "data".
 *
 * @param items
 * @param collectionSlug
 * @param collectionConfigs
 * @returns
 */
export const prepareExportData = (items: PayloadEntity[], collectionSlug: CollectionSlug, collectionConfigs: ClientCollectionConfig[]): ExportableData => {
  const collection = findObject(collectionConfigs, 'slug', collectionSlug);

  if (! collection) {
    const list = flattenObjectList(items);
    return { 'Dados': Object.fromEntries(list.map((v, i) => [i, v])) };
  }

  return addItemRowToData({} as ExportableData, items, collection, collectionConfigs);
}

/**
 * Create a Document with exported data.
 *
 * @param items
 * @param collectionSlug
 * @param collectionConfigs
 * @param filename The filename. May have "<TIMESTAMP>" placeholder to current date (yyyyMMddHHmmss)
 */
export const createExportDocument = (items: PayloadEntity[], collectionSlug: CollectionSlug, collectionConfigs: ClientCollectionConfig[], filename?: string, options?: XLSX.WritingOptions) => {
  const data = prepareExportData(items, collectionSlug, collectionConfigs);

  const workbook = XLSX.utils.book_new();

  for (const sheet in data) {
    const sheedData = XLSX.utils.json_to_sheet(Object.values(data[sheet]));

    // Get only last 2 levels for name
    const sheetParts = sheet.split(' - ');
    const sheetName = abbreviate(sheetParts.slice(-2).join(' - '), 31);

    XLSX.utils.book_append_sheet(workbook, sheedData, sheetName);
  }

  filename = filename || `${collectionSlug}-<TIMESTAMP>`;
  filename = filename.replaceAll('<TIMESTAMP>', toStringComparableDate());

  XLSX.writeFile(workbook, `${filename}.xlsx`, options);
}

/**
 * Create a HTML Table with exported data.
 *
 * @param items
 * @param collectionSlug
 * @param collectionConfigs
 */
export const createExportTables = (items: PayloadEntity[], collectionSlug: CollectionSlug, collectionConfigs: ClientCollectionConfig[]): string => {
  const data = prepareExportData(items, collectionSlug, collectionConfigs);
  const sheets = new Array();

  for (const sheet in data) {
    const sheedData = XLSX.utils.json_to_sheet(Object.values(data[sheet]));

    // Get only last 2 levels for name
    const sheetParts = sheet.split(' - ');
    const sheetName = abbreviate(sheetParts.slice(-2).join(' - '), 31);

    sheets.push(XLSX.utils.sheet_to_html(sheedData, { header: '<h3>' + sheetName + '</h3>', footer: '<br>' }));
  }

  return sheets.join('');
}
